import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, CssBaseline, Grid, Link, Typography } from '@mui/material';
import { ProfileSVG, LoginSVG } from '@ui/components';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import LanguageSelector from '@ui/components/languageSelector';
import CustomAlert from '@ui/components/Alert';
import CustomInputOTP from '@ui/components/CustomInputOTP';
import { loginService, authService } from '@core/services';
import { createCaptcha, deletCaptcha, showCaptcha } from '@core/helpers';
import platforms from '@core/constants/users/platforms';
import FLOW_TYPES from '@core/constants/auth/flowTypes';
import { ROUTES } from '@core/constants/routes/routes';
import { palette } from '@ui/styles/theme';
import '@ui/styles/sass/auth/login.scss';
import '@ui/styles/sass/auth/otp.scss';

const OTP_LENGTH = 6;
// Must match the key used in Password.js (PASSWORD flow OTP handoff).
const PASSWORD_RECOVERY_KEY = 'passwordRecovery';
const REDIRECT_DELAY_MS = 3000;
// Required placeholder in the auth payloads
const NO_REPLY_EMAIL = 'no-reply@backoffice.com';
const SITE_KEY = process.env.RECAPTCHA_SITE_KEY || '6Lfix_AoAAAAAGcDNPhGUpKaHqJsc3ZUm0SUb9jk';
const CAPTCHA_ACTIONS = {
    [FLOW_TYPES.ONBOARDING]: 'VALIDATE',
    [FLOW_TYPES.PASSWORD]: 'FORGOT_VALIDATE',
    [FLOW_TYPES.EMAIL_CHANGE]: 'EMAIL_VALIDATE'
};

const ERROR_KEYS = {

    expired: ['otpExpired', 'otp_expired'],
    notExpired: 'otp_not_expired',
    alreadySent: 'newer_otp_exists'
};

const getErrorKey = (error) => error?.response?.data?.extra?.key;

const isExpiredError = (error) => {
    if (ERROR_KEYS.expired.includes(getErrorKey(error))) return true;
    const data = error?.response?.data || {};
    return `${data.message || ''} ${data.error || ''}`.toLowerCase().includes('expired');
};

const RESEND_SERVICES = {
    [FLOW_TYPES.EMAIL_CHANGE]: (idToken) => authService.resendEmail({ idToken }),
    [FLOW_TYPES.PASSWORD]: (idToken) => loginService.resendPassword({ idToken })
};

const extractNewIdToken = (response) => {
    if (response?.idToken) return String(response.idToken);
    const url = response?.url;
    if (!url || !url.includes('?')) return null;
    return new URLSearchParams(url.slice(url.indexOf('?'))).get('_');
};

export default function OTPPage ({ onSubmit, onResend, title, subtitle }) {
    const { t } = useTranslation();
    const history = useHistory();
    const { search } = useLocation();
    const query = new URLSearchParams(search);

    // Backend sends the PASSWORD flow as `flowtype` (lowercase); onboarding /
    // email-change use `flowType`. Accept both so the flow is always detected.
    const flow = query.get('flowType') || query.get('flowtype') || query.get('flow');
    const urlIdExternal = query.get('_');
    const urlIdToken = query.get('idToken');

    const isAutonomous = !!flow && !onSubmit;
    // The resend endpoints take the OTP id: EMAILCHANGE carries it as `idToken`,
    // the other flows as `_`.
    const resendIdToken = flow === FLOW_TYPES.EMAIL_CHANGE ? urlIdToken : urlIdExternal;

    const [otp, setOtp] = useState('');
    // Translation key for the inline message shown under the OTP boxes ('' = no error).
    const [otpErrorKey, setOtpErrorKey] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    // Resend is offered only after the backend tells us the code expired, and
    // only once (the backend allows a single resend per token).
    const [isExpired, setIsExpired] = useState(false);
    const [resendUsed, setResendUsed] = useState(false);

    const redirectTimerRef = useRef(null);
    // eslint-disable-next-line no-unused-vars
    const [scriptCaptcha, setScriptCaptcha] = useState('');
    const captchaTokenRef = useRef('');

    // Alert states
    const [alert, setAlert] = useState({
        severity: '',
        title: '',
        description: '',
        className: ''
    });
    const [openAlert, setOpenAlert] = useState(false);

    const showAlert = (props) => {
        setAlert(props);
        setOpenAlert(true);
    };

    useEffect(() => {
        // Only the standalone URL-driven OTP screen forwards a captcha token, so
        // only inject/show the badge there (controlled mode delegates to onSubmit).
        if (isAutonomous) {
            createCaptcha(setScriptCaptcha, SITE_KEY);
            showCaptcha();
        }
        return () => {
            clearTimeout(redirectTimerRef.current);
            if (isAutonomous) deletCaptcha();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * An expired code is shown as such and unlocks the resend action; anything
     * else stays a plain "wrong code" inline error.
     */
    const handleValidationError = (error) => {
        const expired = isExpiredError(error);
        if (expired) setIsExpired(true);
        setOtpErrorKey(expired ? 'login.otp.expired' : 'login.otp.codeError');
        setOtp('');
    };

    const runAutonomousFlow = () => {
        if (flow === FLOW_TYPES.ONBOARDING) {
            const data = {
                idPlatform: platforms.backoffice,
                email: NO_REPLY_EMAIL,
                idExternal: urlIdExternal,
                token: otp,
                captchaToken: captchaTokenRef.current
            };

            loginService
                .validate(data)
                .then(() => {
                    history.push(
                        `${ROUTES.AUTH.PASSWORD}?flowType=${FLOW_TYPES.ONBOARDING}&token=${otp}&_=${urlIdExternal}`
                    );
                })
                .catch(handleValidationError)
                .finally(() => setIsLoading(false));
        }

        if (flow === FLOW_TYPES.EMAIL_CHANGE) {
            const data = {
                token: otp,
                idToken: urlIdToken,
                captchaToken: captchaTokenRef.current
            };

            authService
                .validateEmail(data)
                .then(() => {
                    showAlert({ severity: 'success', title: t('login.otp.emailChange.success'), className: 'clientsMC_Alert--success' });
                    redirectTimerRef.current = setTimeout(() => history.push(ROUTES.AUTH.LOGIN), REDIRECT_DELAY_MS);
                })
                .catch(handleValidationError)
                .finally(() => setIsLoading(false));
        }

        if (flow === FLOW_TYPES.PASSWORD) {
            const idToken = Number(urlIdExternal);
            const data = {
                idPlatform: platforms.backoffice,
                email: NO_REPLY_EMAIL,
                metadata: { token: otp },
                idToken,
                captchaToken: captchaTokenRef.current
            };

            loginService
                .validateForgot(data)
                .then(() => {
                    // Hand the validated OTP + idToken to the Password screen via
                    // sessionStorage so they don't travel in the URL. Both the
                    // OTP validation and the password change reuse this token.
                    sessionStorage.setItem(PASSWORD_RECOVERY_KEY, JSON.stringify({ token: otp, idToken }));
                    history.push(`${ROUTES.AUTH.PASSWORD}?flowType=${FLOW_TYPES.PASSWORD}`);
                })
                .catch(handleValidationError)
                .finally(() => setIsLoading(false));
        }
    };

    const handleAutonomousSubmit = () => {
        if (![FLOW_TYPES.ONBOARDING, FLOW_TYPES.EMAIL_CHANGE, FLOW_TYPES.PASSWORD].includes(flow)) return;
        setIsLoading(true);
        setOtpErrorKey('');

        // reCAPTCHA Enterprise: get a token and forward it to the validation
        // endpoint as captchaToken (mirrors Login.js / Password.js).
        window.grecaptcha.enterprise.ready(() => {
            window.grecaptcha.enterprise
                .execute(SITE_KEY, { action: CAPTCHA_ACTIONS[flow] })
                .then(tok => {
                    captchaTokenRef.current = tok;
                    runAutonomousFlow();
                })
                .catch(() => {
                    setIsLoading(false);
                    showAlert({
                        severity: 'error',
                        title: t('errors.generalTitle'),
                        description: t('errors.generalDescription'),
                        className: ''
                    });
                });
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isAutonomous) {
            handleAutonomousSubmit();
            return;
        }

        if (!onSubmit) return;
        setIsLoading(true);
        try {
            await onSubmit(otp);
        }
        catch (error) {
            setOtpErrorKey('login.otp.codeError');
            setOtp('');
        }
        finally {
            setIsLoading(false);
        }
    };

    /**
     * Points the screen at the OTP the resend just created, so the user can type
     * the new code without going through the email link again.
     */
    const applyNewIdToken = (newIdToken) => {
        const params = new URLSearchParams(search);
        params.set(flow === FLOW_TYPES.EMAIL_CHANGE ? 'idToken' : '_', newIdToken);
        history.replace(`${ROUTES.AUTH.OTP}?${params.toString()}`);
    };

    const handleResend = async () => {
        if (resendUsed || isLoading) return;
        setIsLoading(true);
        // Single use: locked from the first click whatever the outcome. If it
        // fails the user can always reopen the link from the email.
        setResendUsed(true);

        try {
            if (onResend) {
                await onResend();
            }
            else {
                const response = await RESEND_SERVICES[flow](Number(resendIdToken));
                const newIdToken = extractNewIdToken(response);
                if (newIdToken) applyNewIdToken(newIdToken);
            }
            setOtp('');
            setOtpErrorKey('');
            showAlert({ severity: 'success', title: t('login.otp.resend.success'), description: '', className: 'clientsMC_Alert--success' });
        }
        catch (error) {
            const key = getErrorKey(error);
            // The backend refuses to resend while a valid code still exists, so
            // there is nothing left to send: keep the action locked either way.
            if (key === ERROR_KEYS.alreadySent || key === ERROR_KEYS.notExpired) {
                setOtpErrorKey(key === ERROR_KEYS.alreadySent
                    ? 'login.otp.resend.alreadySent'
                    : 'login.otp.resend.notExpired');
            }
            else {
                showAlert({ severity: 'error', title: t('errors.generalTitle'), description: t('errors.generalDescription'), className: '' });
            }
        }
        finally {
            setIsLoading(false);
        }
    };

    const handleOtpChange = (val) => {
        setOtp(val);
        if (otpErrorKey) setOtpErrorKey('');
    };

    const isSubmitDisabled = otp.length !== OTP_LENGTH || isLoading;
    // The resend action only appears once the backend reported the code as
    // expired, and only where this flow actually has a way to resend it.
    const canResend = isExpired && (!!onResend || !!(RESEND_SERVICES[flow] && resendIdToken));
    const otpError = otpErrorKey ? t(otpErrorKey) : '';

    return (
        <Grid container component="main" style={{ height: '100vh' }}>
            <CustomAlert
                openAlert={openAlert}
                setOpenAlert={setOpenAlert}
                alert={alert}
                setAlert={setAlert}
            />
            <CssBaseline />

            {/* Left panel */}
            <Grid className="natura-login-left" item xs={6} sm={6} md={6}>
                <LoginSVG />
            </Grid>

            {/* Right panel */}
            <Grid
                className="natura-login-right"
                item
                xs={6}
                sm={6}
                md={6}
                sx={{ position: 'relative' }}
            >
                <LanguageSelector className={{ top: 4, right: 4, position: 'absolute' }} />

                <Box className="login" sx={{ maxWidth: '100%' }}>
                    <Box
                        component="form"
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-start"
                        className="login_card"
                        onSubmit={handleSubmit}
                    >
                        {/* Header */}
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="flex-start"
                            justifyContent="space-between"
                            height="10rem"
                            marginBottom="1.231rem"
                        >
                            <Box className="login_chip">
                                <span className="login_chip--text">BACK OFFICE</span>
                            </Box>
                            <Box className="login_profile--icon">
                                <ProfileSVG />
                            </Box>
                        </Box>

                        {/* Title */}
                        <Typography className="otp_card--title">
                            {title || t('login.otp.title')}
                        </Typography>

                        {/* Subtitle */}
                        <Typography className="otp_card--subtitle">
                            {subtitle || t('login.otp.subtitle')}
                        </Typography>

                        {/* OTP input */}
                        <CustomInputOTP
                            value={otp}
                            onChange={handleOtpChange}
                            error={otpError}
                            hasError={!!otpError}
                            setError={() => setOtpErrorKey('')}
                            length={OTP_LENGTH}
                            label={t('login.otp.inputLabel')}
                        />

                        {/* Resend — shown only once the code expired, usable once */}
                        {canResend && (
                            <>
                                <Typography className="otp_resend--text">
                                    {t('login.otp.resend.question')}
                                </Typography>
                                <Link
                                    className={`otp_resend--link${resendUsed || isLoading ? ' disabled' : ''}`}
                                    variant="body2"
                                    onClick={handleResend}
                                >
                                    {resendUsed
                                        ? t('login.otp.resend.sent')
                                        : t('login.otp.resend.action')
                                    }
                                </Link>
                            </>
                        )}

                        {/* Submit */}
                        <Box marginTop="auto" width="100%">
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                className="login_submit--button"
                                disabled={isSubmitDisabled}
                            >
                                {isLoading
                                    ? <CircularProgress size={24} style={{ color: palette.white }} />
                                    : t('login.otp.submit')
                                }
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Grid>
        </Grid>
    );
}
